import { useEffect } from "react";
import axios from "axios";
import { devWarn } from "@/lib/devlog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Get or create a persistent session id in localStorage. Simple, no cookies. */
function getSessionId() {
    if (typeof window === "undefined") return "server";
    try {
        let sid = window.localStorage.getItem("itly_sid");
        if (!sid) {
            sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
            window.localStorage.setItem("itly_sid", sid);
        }
        return sid;
    } catch {
        return `nolocal-${Math.random().toString(36).slice(2, 10)}`;
    }
}

function getParam(name) {
    if (typeof window === "undefined") return null;
    try {
        const p = new URLSearchParams(window.location.search);
        const v = p.get(name);
        return v ? v.trim() : null;
    } catch {
        return null;
    }
}

/**
 * Check if the user has expressed cookie consent via Iubenda.
 * Returns true if consent was given (measurement/analytics purpose).
 * Returns false if user declined OR hasn't decided yet.
 *
 * Iubenda exposes `_iub.cs.consent.purposes` after preference is expressed.
 * Purpose 4 = "Measurement" (analytics) in the standard Iubenda taxonomy.
 */
function hasIubendaConsent() {
    if (typeof window === "undefined") return false;
    try {
        const iub = window._iub && window._iub.cs;
        if (!iub || !iub.consent) return false;
        // If user hasn't expressed preference yet, don't track
        if (iub.consent.timestamp === undefined && !iub.api?.isPreferenceExpressed?.()) return false;
        // Check specific purposes — 4 is measurement/analytics
        const purposes = iub.consent.purposes || {};
        if (purposes[4] === false) return false;
        return true;
    } catch {
        return false;
    }
}

/**
 * Fire an analytics pageview only if the user has granted analytics consent
 * (via Iubenda). If consent isn't yet given, register a one-shot listener
 * for when preference is expressed and fire only if accepted.
 * No cookies. No PII. Bot filtering handled server-side.
 */
export function useTrackPageView(path) {
    useEffect(() => {
        if (typeof window === "undefined") return undefined;

        // Capture referral code from URL — allowed pre-consent (functional / attribution)
        const ref = getParam("ref");
        if (ref) {
            try {
                window.localStorage.setItem("itly_ref", ref.toUpperCase().slice(0, 16));
            } catch (e) { devWarn("[analytics] cannot persist referral code:", e); }
        }

        const buildPayload = () => ({
            path: path || window.location.pathname,
            referrer: document.referrer || "",
            session_id: getSessionId(),
            utm_source: getParam("utm_source"),
            utm_medium: getParam("utm_medium"),
            utm_campaign: getParam("utm_campaign"),
            ref: getParam("ref"),
        });

        const sendTracking = () => {
            axios
                .post(`${BACKEND_URL}/api/analytics/pageview`, buildPayload())
                .catch(() => { /* silent */ });
        };

        // If consent already given, fire immediately
        if (hasIubendaConsent()) {
            sendTracking();
            return undefined;
        }

        // Otherwise wait for the user to express a preference.
        // Iubenda dispatches `iubenda_consent_given` on the window when consent is granted.
        let fired = false;
        const onConsent = () => {
            if (fired) return;
            if (hasIubendaConsent()) {
                fired = true;
                sendTracking();
            }
        };

        window.addEventListener("iubenda_consent_given", onConsent);

        // Poll briefly in case Iubenda updates the consent object without emitting event
        const poll = setInterval(() => {
            if (fired) {
                clearInterval(poll);
                return;
            }
            if (hasIubendaConsent()) {
                fired = true;
                sendTracking();
                clearInterval(poll);
            }
        }, 1500);

        // Stop polling after 5 minutes to avoid running forever
        const stop = setTimeout(() => clearInterval(poll), 5 * 60 * 1000);

        return () => {
            window.removeEventListener("iubenda_consent_given", onConsent);
            clearInterval(poll);
            clearTimeout(stop);
        };
    }, [path]);
}

/** Read the persisted referral code (set on page load when ?ref=XXX is in URL). */
export function getStoredReferral() {
    if (typeof window === "undefined") return null;
    try {
        return window.localStorage.getItem("itly_ref");
    } catch {
        return null;
    }
}
